import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Form, FormInput, FormItemGroup, FormLabel, FormError, SubmitButton } from './Form';

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

export const SignupForm: React.FC = () => {
  const { signup, isLoading, setErrorMessage, setSuccessMessage, setIsLoading } = useAuth();
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<SignupFormData>();

  const password = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    
    try {
      const result = await signup(data.email, data.password);
      reset();
      // Don't navigate to dashboard - user needs to verify email first
      // They'll see the success message about email verification
    } catch (error) {
      // Error is handled by the auth context
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormItemGroup>
        <FormLabel>E-mail</FormLabel>
        <FormInput
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address',
            },
          })}
          type="email"
          disabled={isLoading}
          placeholder="Enter your email"
        />
        {errors.email && <FormError>{errors.email.message}</FormError>}
      </FormItemGroup>
      

      
      <FormItemGroup>
        <FormLabel>Password</FormLabel>
        <FormInput
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: 6,
              message: 'Password must be at least 6 characters',
            },
          })}
          type="password"
          disabled={isLoading}
          placeholder="Enter your password"
        />
        {errors.password && <FormError>{errors.password.message}</FormError>}
      </FormItemGroup>
      
      <FormItemGroup>
        <FormLabel>Confirm Password</FormLabel>
        <FormInput
          {...register('confirmPassword', {
            required: 'Please confirm your password',
            validate: (value) => value === password || 'Passwords do not match',
          })}
          type="password"
          disabled={isLoading}
          placeholder="Confirm your password"
        />
        {errors.confirmPassword && <FormError>{errors.confirmPassword.message}</FormError>}
      </FormItemGroup>
      
      <FormItemGroup>
        <SubmitButton type="submit" disabled={isLoading}>
          {isLoading ? 'Signing up...' : 'Sign up'}
        </SubmitButton>
      </FormItemGroup>
    </Form>
  );
};
